import { Component } from "react";
import { connect } from "react-redux";
import { Form, formValueSelector, type InjectedFormProps, reduxForm } from "redux-form";
import { type AsyncValidateCallback } from "redux-form/lib/reduxForm";
import { Callout, CalloutIntent } from "widgets";

import {
  getOrderedListOfTrackerAndWorkflowAttributes,
  type IPlaybookExecutionState,
  isNgsDataExperimentAttribute,
  updatePlaybookExecutionState,
} from "@/redux/modules/playbook-execution.ts";
import {
  checkAttributeListUniqueness,
  type IAttributesTemplatesLookup,
  type IDefaultValuesLookup,
  type ISampleEditingState,
  resetSampleGrid,
  updateSampleEditingState,
} from "@/redux/modules/sample-editing.ts";
import { type IAppState } from "@/redux/reducer.ts";
import {
  ATTRIBUTE_TYPE,
  MAX_LENGTH,
  PLAYBOOK_TYPE,
  PRIMARY_PARENT_TYPE_MISSING,
  REPRESENTER_COLUMN,
  SAMPLE_TRACKING,
} from "@/settings/settings.ts";

import {
  getFormError,
  getFormWarning,
  handleTabInForm,
  type ISelectOption,
  simpleDropdownFieldValidation,
} from "../../../app-utils/form.js";
import {
  type IConfiguredAttribute,
  type IConfiguredPlaybook,
  type ISampleRegistrationPlaybookConfig,
} from "../../../app-utils/playbook.js";
import {
  fieldIsConfiguredInPlaybook,
  getPrimaryParentType,
  getUniqueAttributeId,
  mandatoryFieldHasNoDefault,
} from "../../../app-utils/sample.js";
import { isAttributeAutomatic, resolveFormFieldTemplatesDynamically } from "../../../app-utils/sample-template.js";
import {
  type Attribute,
  type ParentType,
  type ParentTypeSet,
  type Project,
  type SampleStatus,
} from "../../../graphql.js";
import { getProjectLabel } from "../../../redux/modules/config.js";
import { showErrorPage } from "../../../redux/modules/errors.js";
import AttributeInputField from "../../core/forms/AttributeInputField.js";
import ProjectsSelect from "../../core/forms/ProjectsSelect.js";
import TabFence from "../../core/forms/TabFence.js";
import CheckboxField, { CheckboxReduxFormField } from "../../core/redux-form/CheckboxField.js";
import InputField, { InputReduxFormField } from "../../core/redux-form/InputField.js";
import ReactSelectField, { ReactSelectReduxFormField } from "../../core/redux-form/ReactSelectField.js";
import { FieldTitle } from "../FieldTitle.js";
import PlaybookWizardStep, {
  type IPlaybookWizardStepInjectedProps,
  type IPlaybookWizardStepProvidedProps,
} from "../PlaybookWizardStep.js";

export const RECYCLE_FIELD_NAME = "__recycle__";
export const RECYCLE_VALUE_FIELD_NAME = "__recycle_value__";
export const FIELD_PROJECT = "project";

export const SAMPLE_TRACKING_OPTIONS: ISelectOption[] = [
  { label: "Analysis of existing samples", value: SAMPLE_TRACKING.USE_EXISTING },
  { label: "Register new samples", value: SAMPLE_TRACKING.REGISTER },
];

type ICombinedRegistrationNgsBasicsScreenOwnProps = IPlaybookWizardStepProvidedProps;

interface ICombinedRegistrationNgsBasicsScreenAppStateProps {
  sampleEditingState: ISampleEditingState;
  playbookExecution: IPlaybookExecutionState;
  playbookConfig: IConfiguredPlaybook;
  globalFormError: string;
  globalFormWarning: string;
  recycle: boolean; // driver for showing or hiding the parent attributes in case it is recyclable
  //asyncBlurFields?: Array<string>; // from redux-form's ConfigProps<any, any>
  templatesLookup: IAttributesTemplatesLookup;
  fullPlaybookConfigState: ISampleRegistrationPlaybookConfig;
  defaultValuesLookup: IDefaultValuesLookup;
  selectedProject: Project;
  selectedSampleTracking: ISelectOption;
  usingPreselectedSamples: boolean;
}

interface ICombinedRegistrationNgsBasicsScreenDispatchProps {
  updateSampleEditingState: (state: Partial<ISampleEditingState>) => void;
  updatePlaybookExecutionState: (state: Partial<IPlaybookExecutionState>) => void;
  showError: (errorMsg) => void;
  checkAttributesUniqueness: (
    attributes: Attribute[],
    values: Record<string, string> /* map by fieldId */,
  ) => Promise<string[]>;
  resetSampleGrid: () => void;
}

export interface ICombinedRegistrationNgsBasicsScreenProps
  extends ICombinedRegistrationNgsBasicsScreenAppStateProps,
    ICombinedRegistrationNgsBasicsScreenDispatchProps,
    ICombinedRegistrationNgsBasicsScreenOwnProps,
    InjectedFormProps<FormData, any>,
    IPlaybookWizardStepInjectedProps {
  // only if this screen is a redux-form
}

function mapStateToProps(
  state: IAppState,
  _ownProps: ICombinedRegistrationNgsBasicsScreenProps,
): ICombinedRegistrationNgsBasicsScreenAppStateProps {
  const sampleEditingState = state.sampleEditingState;
  const playbookExecutionState = state.playbookExecution;
  const sampleType = sampleEditingState.sampleType;
  //const primaryParentType: ParentType = getPrimaryParentType(sampleType);
  //const uniqueConstraintAttributes =
  //  primaryParentType?.parentAttributes.filter((attribute: Attribute) => attribute.valueUniqueConstraint) || [];

  return {
    sampleEditingState: sampleEditingState,
    playbookExecution: playbookExecutionState,
    playbookConfig: playbookExecutionState.playbook,
    globalFormError: getFormError(CombinedRegistrationNgsBasicsScreen.COMBINED_REGISTRATION_NGS_PARENT_SCREEN, state),
    globalFormWarning:
      getFormWarning(CombinedRegistrationNgsBasicsScreen.COMBINED_REGISTRATION_NGS_PARENT_SCREEN, state) ||
      playbookExecutionState.contentUnavailableWarning,
    recycle: formValueSelector(CombinedRegistrationNgsBasicsScreen.COMBINED_REGISTRATION_NGS_PARENT_SCREEN)(
      state,
      RECYCLE_FIELD_NAME,
    ),
    //asyncBlurFields: uniqueConstraintAttributes.map((attribute: Attribute) => getUniqueAttributeId(attribute)),
    templatesLookup: sampleType && sampleEditingState.templatesLookup[sampleType.id],
    fullPlaybookConfigState: playbookExecutionState.playbook.config,
    defaultValuesLookup: state.sampleEditingState.defaultValuesLookup,
    usingPreselectedSamples: playbookExecutionState.startedFromSelectedSamples,
    selectedProject:
      playbookExecutionState.project ||
      formValueSelector(CombinedRegistrationNgsBasicsScreen.COMBINED_REGISTRATION_NGS_PARENT_SCREEN)(
        state,
        FIELD_PROJECT,
      ),
    selectedSampleTracking: formValueSelector(
      CombinedRegistrationNgsBasicsScreen.COMBINED_REGISTRATION_NGS_PARENT_SCREEN,
    )(state, CombinedRegistrationNgsBasicsScreenImpl.FIELD_SAMPLE_TRACKING),
  };
}

function mapDispatchToProps(dispatch): ICombinedRegistrationNgsBasicsScreenDispatchProps {
  return {
    updateSampleEditingState: (state: Partial<ISampleEditingState>) => dispatch(updateSampleEditingState(state)),
    updatePlaybookExecutionState: (state: Partial<IPlaybookExecutionState>) =>
      dispatch(updatePlaybookExecutionState(state)),
    showError: (errorMsg) => dispatch(showErrorPage(PRIMARY_PARENT_TYPE_MISSING, errorMsg, null)),
    checkAttributesUniqueness: (attributes: Attribute[], values: Record<string, string>) =>
      dispatch(checkAttributeListUniqueness(attributes, values)),
    resetSampleGrid: () => dispatch(resetSampleGrid()),
  };
}

/**
 * Screen used in sample registration wizard for parent attributes input.
 */
class CombinedRegistrationNgsBasicsScreenImpl extends Component<ICombinedRegistrationNgsBasicsScreenProps> {
  public static COMBINED_REGISTRATION_NGS_PARENT_SCREEN = "COMBINED_REGISTRATION_NGS_PARENT_SCREEN";
  public static FIELD_SAMPLE_TRACKING = "SAMPLE_TRACKING";
  public static FIELD_EVENT_NAME = "EVENT_NAME";
  public static FIELD_EVENT_DESCRIPTION = "EVENT_DESCRIPTION";

  private lastTabRef;
  private firstTabRef;

  constructor(props) {
    super(props);
  }

  componentDidMount() {
    const conditionalDefault =
      this.props.playbookConfig.type.id === PLAYBOOK_TYPE.NGS_ANALYSIS_WITH_REGISTERED_SAMPLES
        ? {
            [CombinedRegistrationNgsBasicsScreenImpl.FIELD_SAMPLE_TRACKING]:
              SAMPLE_TRACKING_OPTIONS.find((s) => s.value === this.props.playbookExecution?.sampleTracking) ||
              SAMPLE_TRACKING_OPTIONS[0],
          }
        : {};

    this.props.initialize({
      ...(this.props.sampleEditingState.experimentValues || this.props.sampleEditingState.defaultValuesLookup),
      ...conditionalDefault,
      [FIELD_PROJECT]: this.props.selectedProject,
    });
  }

  getStatus = (statusId: number): SampleStatus => {
    const sampleStatuses: SampleStatus[] = this.props.sampleEditingState.sampleType?.statusSet.statuses;
    return sampleStatuses?.length > 0 ? sampleStatuses.find((s: SampleStatus) => s.id === statusId) : null;
  };

  handleSubmitData = (values: Record<string, any>) => {
    // at this stage the validation has succeeded
    const selectedProject: Project = values[FIELD_PROJECT];

    const selectedStatus: SampleStatus = this.getStatus(
      this.props.fullPlaybookConfigState.sampleStatus.afterSubmission,
    );

    const eventName: string = values[CombinedRegistrationNgsBasicsScreenImpl.FIELD_EVENT_NAME];
    const eventDescription: string = values[CombinedRegistrationNgsBasicsScreenImpl.FIELD_EVENT_DESCRIPTION];
    const selectedSampleTracking: ISelectOption = values[CombinedRegistrationNgsBasicsScreenImpl.FIELD_SAMPLE_TRACKING];

    // complete the values for "false" booleans in order to show them correctly in the review screen
    const { attributes } = this.retrieveRelevantAttributes();
    attributes.forEach((a: Attribute) => {
      const uniqueIdentifier: string = getUniqueAttributeId(a);
      if (a.attributeType.id === ATTRIBUTE_TYPE.BOOLEAN && values[uniqueIdentifier] == null) {
        values[uniqueIdentifier] = false;
      }
    });

    this.props.updatePlaybookExecutionState({
      eventName,
      eventDescription,
      project: selectedProject,
      sampleTracking: this.props.usingPreselectedSamples ? SAMPLE_TRACKING.USE_EXISTING : selectedSampleTracking?.value,
    });

    this.props.updateSampleEditingState({
      experimentValues: values,
      status: selectedStatus,
    });

    this.props.proceedWithNextStep();
  };

  pageDescription = () => {
    const playbookType: number = this.props.playbookConfig.type.id;
    switch (playbookType) {
      case PLAYBOOK_TYPE.SAMPLE_REGISTRATION:
        return `Select a destination for the registered samples.`;
      case PLAYBOOK_TYPE.NGS_ANALYSIS:
        return `Select a destination for the files and results that are generated for this analysis.`;
      case PLAYBOOK_TYPE.NGS_ANALYSIS_WITH_REGISTERED_SAMPLES:
        return `Select a destination for the samples, files and results that are generated for this analysis.`;
    }
  };

  retrieveRelevantAttributes = () => {
    const parentAttributes: Attribute[] = this.props.sampleEditingState.experimentAttributes;

    const orderedListOfParentAttributes: IConfiguredAttribute[] = getOrderedListOfTrackerAndWorkflowAttributes(
      this.props.fullPlaybookConfigState.experimentInput,
      parentAttributes,
    );

    const misConfiguredAttributes: Attribute[] = parentAttributes.filter(
      (attribute: Attribute) =>
        (!attribute.automatic || attribute.representedColumn === REPRESENTER_COLUMN.NAME) &&
        !fieldIsConfiguredInPlaybook(attribute, this.props.fullPlaybookConfigState.experimentInput) &&
        mandatoryFieldHasNoDefault(attribute, this.props.defaultValuesLookup),
    );

    // the selection  does not contain all the required data (e.g. stored only the identifiers or so) therefore we need to flesh out this selection with the available data
    let attributes: Attribute[] = orderedListOfParentAttributes.concat(misConfiguredAttributes); // add the misconfigured attributes so that they can be filled with a valid value

    if (this.props.recycle) {
      // in this case remove all of the tracker attributes, since we will take the values of the selected recycled parent
      attributes = [];
    }
    return { attributes, misConfiguredAttributes };
  };

  render() {
    const isRecyclable: boolean = this.props.sampleEditingState.sampleType?.parentTypeSets.find(
      (parentTypeSet: ParentTypeSet) => parentTypeSet.isPrimary,
    ).isRecyclable;

    const parentType: ParentType = this.props.sampleEditingState.parentType;

    const { attributes, misConfiguredAttributes } = this.retrieveRelevantAttributes();

    return (
      <PlaybookWizardStep
        {...this.props}
        playbookPage={{
          ...this.props.playbookPage,
          label: this.props.playbookPage.label || parentType?.name || "Playbook Outputs",
          description: this.pageDescription(),
        }}
        wizardFooterData={{
          error: this.props.globalFormError || this.props.error,
          warning: this.props.globalFormWarning,
          nextButtonProps: {
            label: "Next",
            isLoading: !!this.props.asyncValidating,
            onPress: this.props.handleSubmit(this.handleSubmitData),
          },
        }}
      >
        <Form
          className="form-horizontal"
          onSubmit={this.props.handleSubmit(this.handleSubmitData)}
          onKeyDownCapture={handleTabInForm}
        >
          <TabFence targetRef={this.lastTabRef} />

          <ProjectsSelect
            name={FIELD_PROJECT}
            sampleTypeId={this.props.sampleEditingState.sampleType?.id}
            horizontalLayout
            title={<FieldTitle label={getProjectLabel(true, false)} mandatory />}
            tooltip={`Select the ${getProjectLabel(
              false,
              false,
            )} to which the analysis and its output will be attached`}
            onError={(error: Error) => this.props.updateWizardMessage(error.message)}
            info={
              this.props.sampleEditingState.sampleType &&
              "Access to samples is controlled by the sample type settings and not by project membership"
            }
          />
          <InputReduxFormField
            horizontalLayout
            key={"event-name"}
            name={CombinedRegistrationNgsBasicsScreenImpl.FIELD_EVENT_NAME}
            label={<FieldTitle label={"Event name"} />}
            component={InputField}
            placeholder="Enter text..."
            maxLength={MAX_LENGTH.EVENT.NAME}
            tooltip={"You can provide a name for the playbook event"}
          />
          <InputReduxFormField
            horizontalLayout
            key={"event-description"}
            name={CombinedRegistrationNgsBasicsScreenImpl.FIELD_EVENT_DESCRIPTION}
            label={<FieldTitle label={"Event description"} />}
            component={InputField}
            placeholder="Enter text..."
            maxLength={MAX_LENGTH.EVENT.DESCRIPTION}
            tooltip={"You can provide a description for the playbook event"}
          />
          {this.props.playbookConfig.type.id === PLAYBOOK_TYPE.NGS_ANALYSIS_WITH_REGISTERED_SAMPLES &&
            !this.props.usingPreselectedSamples && (
              <ReactSelectReduxFormField
                horizontalLayout={true}
                key={`parent-sample-tracking`}
                name={CombinedRegistrationNgsBasicsScreenImpl.FIELD_SAMPLE_TRACKING}
                isClearable={false}
                isSearchable={true}
                label={
                  <FieldTitle
                    label={"Sample registration"}
                    title="You must select a sample tracking method"
                    mandatory
                  />
                }
                tooltip={`Please select an option for tracking the sample data`}
                placeholder={"Please select..."}
                component={ReactSelectField}
                options={SAMPLE_TRACKING_OPTIONS}
                validate={simpleDropdownFieldValidation}
                handleChange={this.props.resetSampleGrid} // Reset the data grid if going back to basics screen and changing the sample tracking mode,
              />
            )}
          {this.props.selectedSampleTracking?.value !== SAMPLE_TRACKING.USE_EXISTING && (
            <>
              {isRecyclable && (
                // create a radio button for recyclable parentTypes to switch between 'NEW' and "REUSE" mode.
                <CheckboxReduxFormField
                  horizontalLayout={true}
                  key={`parent-recycle-radio-${parentType.id}`}
                  name={RECYCLE_FIELD_NAME}
                  leftLabel={`Use existing ${parentType.name}`}
                  label={""}
                  tooltipText={`Use an existing ${parentType.name} or create a new one`}
                  component={CheckboxField}
                />
              )}

              {this.props.recycle && (
                // meaning the checkbox above has been selected
                <ReactSelectReduxFormField
                  horizontalLayout={true}
                  key={`parent-recycle-attribute-${parentType.id}`}
                  name={RECYCLE_VALUE_FIELD_NAME}
                  isClearable={false}
                  isSearchable={true}
                  label={"Name"}
                  tooltip={`Please select a ${parentType.name}`}
                  placeholder={"Please select..."}
                  component={ReactSelectField}
                  options={parentType.parents.map((x) => ({ label: x.name, value: x.id }))}
                  validate={simpleDropdownFieldValidation}
                />
              )}

              {attributes
                .filter((attribute: Attribute) => !isNgsDataExperimentAttribute(attribute))
                .map((attribute: Attribute) => (
                  <AttributeInputField
                    attribute={{ ...attribute, automatic: isAttributeAutomatic(attribute, this.props.templatesLookup) }}
                    key={`attribute-${attribute.id}`}
                  />
                ))}

              {!this.props.recycle &&
                attributes.length === 0 &&
                this.props.playbookConfig.type.id !== PLAYBOOK_TYPE.NGS_ANALYSIS && (
                  <div>
                    {parentType ? (
                      <div>
                        No attributes of parent type <strong> {parentType.name} </strong> are available in this form
                      </div>
                    ) : (
                      <div>No experiment input is required.</div>
                    )}
                  </div>
                )}

              {misConfiguredAttributes.length > 0 && (
                <Callout intent={CalloutIntent.Warning}>
                  {`Some of the mandatory fields in this form are shown because of missing default configuration in
            "${parentType.name}". Please contact your administrator.`}
                </Callout>
              )}
            </>
          )}
          <TabFence targetRef={this.firstTabRef} />
        </Form>
      </PlaybookWizardStep>
    );
  }

  static async asyncValidate(...params: any[]) {
    const values = params[0]; // because the typescript definition is not up to date !
    // const dispatch = params[1];  // because the typescript definition is not up to date !
    const props: ICombinedRegistrationNgsBasicsScreenProps = params[2]; // because the typescript definition is not up to date !
    // const blurredFields = params[3];  // because the typescript definition is not up to date !

    if (props.recycle) {
      return;
    } else {
      const primaryParentType = getPrimaryParentType(props.sampleEditingState.sampleType);
      if (primaryParentType && !(props.selectedSampleTracking?.value === SAMPLE_TRACKING.USE_EXISTING)) {
        // why not only checking the one parentAttributes with attribute.valueUniqueConstraint ?
        try {
          await props.checkAttributesUniqueness(primaryParentType.parentAttributes, values);
        } catch (e) {
          throw e.message ? { _error: e.message } : e; // either the error comes already formatted for specific field, or it is form-wide error
        }
      }
    }
  }
}

const CombinedRegistrationNgsBasicsScreen = connect<
  ICombinedRegistrationNgsBasicsScreenAppStateProps,
  ICombinedRegistrationNgsBasicsScreenDispatchProps,
  ICombinedRegistrationNgsBasicsScreenOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm<any, ICombinedRegistrationNgsBasicsScreenOwnProps>({
    form: CombinedRegistrationNgsBasicsScreenImpl.COMBINED_REGISTRATION_NGS_PARENT_SCREEN,
    destroyOnUnmount: false,
    asyncValidate: CombinedRegistrationNgsBasicsScreenImpl.asyncValidate,
    onChange: resolveFormFieldTemplatesDynamically,
    // in order to trigger the async validation even if the form is pristine upon submission (can happen if the default is invalid e.g. not unique)
    shouldAsyncValidate: (params: AsyncValidateCallback<FormData, any>) => {
      switch (params.trigger) {
        case "submit": // applies also to manually triggered asyncValidate()
          return true;
        default:
          return false;
      }
    },
  })(CombinedRegistrationNgsBasicsScreenImpl as any) as any,
);
export { CombinedRegistrationNgsBasicsScreen, CombinedRegistrationNgsBasicsScreenImpl };
